import { useEffect, useState } from 'react';
import { claimLink, forgetLoginTry, loginOnce, stashPending, type ClaimResult } from '../data/claim';
import { startLogin } from '../lib/auth';
import { planPath } from '../lib/route';

/* ЭКРАН ПРИЁМА ССЫЛКИ НА ПЛАН — адрес `/care/<секрет>`.

   Пациент попадает сюда из сообщения врача. Дальше ровно один сценарий:
   предъявили секрет → сервер сверил номер аккаунта с номером в карте → план
   открыт этому аккаунту навсегда, и мы уводим на сам план.

   ВХОД — ЧАСТЬ ФЛОУ, А НЕ ОШИБКА. Первый заход почти всегда анонимный: пациент
   открывает ссылку из мессенджера, где он не входил. Поэтому 401 не показывается
   отказом: секрет откладывается, пациент уходит в Keycloak и возвращается сюда
   же — адрес возврата у клиента один (`/login`), и пронести секрет через него
   можно только самим.

   ПОВТОРНОЕ ОТКРЫТИЕ СВОЕЙ ССЫЛКИ РАБОТАЕТ. Ручка идемпотентна для того же
   аккаунта: план уже ваш — значит просто открываем его. Это не край, а обычный
   случай: человек сохранил сообщение и вернулся через неделю. */

export function Claim({ token }: { token: string }) {
    const [result, setResult] = useState<ClaimResult | null>(null);

    useEffect(() => {
        let alive = true;
        claimLink(token).then(async (r) => {
            if (!alive) return;
            if (r.state === 'ok') {
                forgetLoginTry();
                // Замена, а не push: возвращаться «назад» на использованный
                // секрет незачем, и в истории браузера ему не место.
                window.location.replace(planPath(r.channelId));
                return;
            }
            if (r.state === 'anonymous') {
                stashPending(token);
                /* Уходим на вход САМИ, но ровно один раз за секрет: вернувшись
                   сюда с тем же ответом, мы закрутили бы петлю редиректов без
                   единого клика. Второй раз — экран с кнопкой ниже. */
                if (!loginOnce(token)) {
                    if (alive) setResult({ state: 'anonymous' });
                    return;
                }
                // `startLogin` возвращает управление ТОЛЬКО при отказе: успех —
                // это уход со страницы в Keycloak.
                await startLogin();
                if (alive) setResult({ state: 'error' });
                return;
            }
            setResult(r);
        });
        return () => {
            alive = false;
        };
    }, [token]);

    if (result === null) return <div className="plnload">открываем план…</div>;

    return (
        <div className="plnfail">
            <h2 className="clmh">{title(result.state)}</h2>
            <p>{text(result.state)}</p>
            {/* У «нужно войти» действие другое: в кабинет вести некуда, он тоже
                закрыт входом, и пациент получил бы тот же экран с кнопкой. */}
            {result.state === 'anonymous' ? (
                <button type="button" className="plnback" onClick={() => void startLogin()}>
                    войти
                </button>
            ) : (
                <a className="plnback" href="/">
                    в кабинет
                </a>
            )}
        </div>
    );
}

function title(state: ClaimResult['state']): string {
    switch (state) {
        case 'anonymous':
            return 'Нужно войти';
        case 'expired':
            return 'Ссылка больше не действует';
        case 'mismatch':
            return 'Номер не совпал';
        case 'nophone':
            return 'Нечем сверить номер';
        case 'gone':
            return 'План недоступен';
        default:
            return 'Не удалось открыть план';
    }
}

/* Тексты разведены по исходам намеренно. «Не удалось» на все случаи — это ответ,
   после которого пациент не знает, что делать: ждать, просить новую ссылку или
   звонить врачу. Ни один из них не говорит, существовал ли план: про 404 сервер
   отвечает одним телом на четыре разных случая, и раскрывать их здесь нельзя. */
function text(state: ClaimResult['state']): string {
    switch (state) {
        case 'anonymous':
            return 'Чтобы открыть план, войдите по номеру телефона — тому, который вы давали в клинике.';
        case 'expired':
            return 'Возможно, врач выслал новую — откройте последнее сообщение. Если её нет, попросите выслать ссылку заново.';
        case 'mismatch':
            return 'Вы вошли с номером, которого нет в вашей карте у этого врача. Войдите с тем номером, который давали в клинике, или попросите врача проверить номер в карте.';
        case 'nophone':
            return 'У вашего аккаунта или в карте не заполнен телефон, а без номера мы не можем убедиться, что план ваш. Попросите врача проверить номер в карте.';
        case 'gone':
            return 'Ссылка не действует. Попросите врача выслать её заново.';
        case 'neighbour':
            return 'Сервис проверки временно недоступен. План не открыт — попробуйте ещё раз через несколько минут.';
        case 'offline':
            return 'Кабинет не подключён к серверу.';
        default:
            return 'Проверьте связь и попробуйте ещё раз.';
    }
}
